import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import GoogleMapReact from "google-map-react";
import "../styles/HomeScreen.css";
import { Browser } from "@capacitor/browser";
import { Geolocation } from "@capacitor/geolocation";

/**
 * ✅ IMPORTANT:
 * - REACT_APP_API_URL should be like: https://pay2pee.onrender.com
 * - NOT: https://pay2pee.onrender.com/api
 * This block cleans it either way, and ensures we call /api exactly once.
 */
const API_BASE = (() => {
  const clean = (v) =>
    (v || "")
      .trim()
      .replace(/\/+$/, "")
      .replace(/\/api$/, "");

  const envBase = clean(process.env.REACT_APP_API_URL);
  if (envBase) return envBase;

  if (typeof window === "undefined") return "";

  const origin = window.location.origin || "";
  const isCapacitor =
    origin.startsWith("capacitor://") ||
    origin.startsWith("http://localhost") ||
    /localhost|127\.0\.0\.1/.test(origin);

  if (isCapacitor) return "https://pay2pee.onrender.com";
  if (origin.includes("pay2pee.app")) return "https://pay2pee.onrender.com";

  return origin;
})();

const API = `${API_BASE}/api`;

const toNumber = (v) => {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const parsed = parseFloat(v);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const computeDistanceMiles = (lat1, lng1, lat2, lng2) => {
  const nLat1 = toNumber(lat1);
  const nLng1 = toNumber(lng1);
  const nLat2 = toNumber(lat2);
  const nLng2 = toNumber(lng2);

  if (
    nLat1 == null ||
    nLng1 == null ||
    nLat2 == null ||
    nLng2 == null ||
    Number.isNaN(nLat1) ||
    Number.isNaN(nLng1) ||
    Number.isNaN(nLat2) ||
    Number.isNaN(nLng2)
  ) {
    return null;
  }

  const toRad = (v) => (v * Math.PI) / 180;
  const R = 3958.8; // miles
  const φ1 = toRad(nLat1);
  const φ2 = toRad(nLat2);
  const dφ = toRad(nLat2 - nLat1);
  const dλ = toRad(nLng2 - nLng1);

  const a =
    Math.sin(dφ / 2) * Math.sin(dφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) * Math.sin(dλ / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const getDistanceLabel = (loc, userLocation) => {
  if (!loc) return "";
  if (loc.distanceText) return loc.distanceText;

  const milesField =
    loc.distanceMiles !== undefined && loc.distanceMiles !== null
      ? loc.distanceMiles
      : loc.distance !== undefined && loc.distance !== null
      ? loc.distance
      : null;

  if (milesField !== null) {
    const milesNum = toNumber(milesField);
    if (milesNum != null && !Number.isNaN(milesNum)) {
      return `${milesNum.toFixed(1)} mi away`;
    }
  }

  if (
    userLocation &&
    loc.coordinates &&
    loc.coordinates.lat !== undefined &&
    loc.coordinates.lng !== undefined
  ) {
    const miles = computeDistanceMiles(
      userLocation.lat,
      userLocation.lng,
      loc.coordinates.lat,
      loc.coordinates.lng
    );
    if (miles != null && !Number.isNaN(miles)) return `${miles.toFixed(1)} mi away`;
  }

  return "";
};

const getPriceValue = (loc) => {
  if (!loc) return null;
  if (typeof loc.price === "number") return loc.price;
  if (typeof loc.pricePerVisit === "number") return loc.pricePerVisit;
  if (typeof loc.priceCents === "number") return loc.priceCents / 100;
  return null;
};

const getLocationName = (loc) => {
  if (!loc) return "Restroom";
  return loc.businessName || loc.name || loc.locationName || "Restroom";
};

// Map pin
const MapMarker = ({ selected, onClick }) => (
  <button
    type="button"
    className={selected ? "p2p-pin p2p-pin--selected" : "p2p-pin"}
    onClick={onClick}
  >
    <img src="/images/logo.png" alt="Pay2Pee" className="p2p-pin__img" />
  </button>
);

// User dot
const UserDot = () => (
  <div className="p2p-userdot" aria-label="Your location">
    <div className="p2p-userdot__ring" />
    <div className="p2p-userdot__core" />
  </div>
);

export default function HomeScreen() {
  const gaEvent = (name, params = {}) => {
    try {
      if (typeof window === "undefined") return;
      if (!window.gtag) return;
      window.gtag("event", name, {
        page_path: window.location.pathname,
        page_location: window.location.href,
        ...params,
      });
    } catch {
      // no-op
    }
  };

  useEffect(() => {
    document.body.classList.add("p2p-home-fullbleed");
    gaEvent("home_view");
    return () => document.body.classList.remove("p2p-home-fullbleed");
  }, []);

  const [locations, setLocations] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [activeLocationId, setActiveLocationId] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [center, setCenter] = useState({ lat: 42.3314, lng: -83.0458 });
  const [userLocation, setUserLocation] = useState(null);

  const mapsKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const [isConfirming, setIsConfirming] = useState(false);
  const [error, setError] = useState("");

  const sheetRef = useRef(null);
  const dragRef = useRef({
    startY: 0,
    startTranslate: 0,
    dragging: false,
  });

  const [sheetTranslate, setSheetTranslate] = useState(380);
  const [sheetSnapPoints, setSheetSnapPoints] = useState({
    expanded: 100,
    collapsed: 520,
  });

  const listRef = useRef(null);
  const rowRefs = useRef({});

  const [showDetails, setShowDetails] = useState(false);
  const [detailsIndex, setDetailsIndex] = useState(0);

  useEffect(() => {
    const compute = () => {
      const vh = window.innerHeight || 800;

      const expanded = Math.max(60, Math.round(vh * 0.10));
      const collapsed = Math.max(320, Math.round(vh * 0.68));

      setSheetSnapPoints({ expanded, collapsed });

      if (!dragRef.current.dragging) setSheetTranslate(collapsed);
    };

    if (typeof window !== "undefined") {
      compute();
      window.addEventListener("resize", compute);
      return () => window.removeEventListener("resize", compute);
    }
  }, []);

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  const onSheetTouchStart = (e) => {
    const touch = e.touches?.[0];
    if (!touch) return;
    dragRef.current.dragging = true;
    dragRef.current.startY = touch.clientY;
    dragRef.current.startTranslate = sheetTranslate;
  };

  const onSheetTouchMove = (e) => {
    if (!dragRef.current.dragging) return;
    const touch = e.touches?.[0];
    if (!touch) return;

    const delta = touch.clientY - dragRef.current.startY;
    const next = dragRef.current.startTranslate + delta;

    const min = sheetSnapPoints.expanded;
    const max = sheetSnapPoints.collapsed;
    setSheetTranslate(clamp(next, min, max));
  };

  const onSheetTouchEnd = () => {
    dragRef.current.dragging = false;

    const { expanded, collapsed } = sheetSnapPoints;
    const mid = (expanded + collapsed) / 2;

    const target = sheetTranslate < mid ? expanded : collapsed;
    setSheetTranslate(target);
  };

  const expandSheet = () => setSheetTranslate(sheetSnapPoints.expanded);
  const collapseSheet = () => setSheetTranslate(sheetSnapPoints.collapsed);

  const getPhotosArray = (loc) => {
    if (!loc) return [];
    const raw = loc.photos;

    if (Array.isArray(raw)) {
      if (typeof raw[0] === "string") return raw.filter(Boolean);

      if (raw[0] && typeof raw[0] === "object" && raw[0].url) {
        return raw.map((p) => p.url).filter(Boolean);
      }
    }

    return [];
  };

  useEffect(() => {
    let isMounted = true;

    const fetchLocations = async () => {
      try {
        setError("");
        const res = await axios.get(`${API}/locations/nearby`);
        const raw = res.data.locations || [];

        let list = raw;
        if (raw.length > 1) {
          const cleaned = raw.filter((loc) => {
            const hasNumericPrice =
              typeof loc.price === "number" ||
              typeof loc.pricePerVisit === "number" ||
              typeof loc.priceCents === "number";
            const flaggedDemo = loc.isDemo || loc.demo === true;
            return hasNumericPrice && !flaggedDemo;
          });
          list = cleaned.length > 0 ? cleaned : raw;
        }

        if (!isMounted) return;

        const sorted = [...list].sort((a, b) => {
          const aDist =
            typeof a.distanceMiles === "number"
              ? a.distanceMiles
              : typeof a.distance === "number"
              ? a.distance
              : userLocation && a.coordinates
              ? computeDistanceMiles(
                  userLocation.lat,
                  userLocation.lng,
                  a.coordinates.lat,
                  a.coordinates.lng
                )
              : Infinity;

          const bDist =
            typeof b.distanceMiles === "number"
              ? b.distanceMiles
              : typeof b.distance === "number"
              ? b.distance
              : userLocation && b.coordinates
              ? computeDistanceMiles(
                  userLocation.lat,
                  userLocation.lng,
                  b.coordinates.lat,
                  b.coordinates.lng
                )
              : Infinity;

          return (aDist ?? Infinity) - (bDist ?? Infinity);
        });

        setLocations(sorted);
        setFiltered(sorted);

        gaEvent("locations_loaded", {
          count: sorted.length,
          has_user_location: !!userLocation,
        });

        if (list.length > 0) {
          setActiveLocationId(list[0].id || null);
          if (list[0].coordinates && list[0].coordinates.lat) {
            setCenter(list[0].coordinates);
          }
        }
      } catch (err) {
        console.error("Failed to fetch locations", err);
        if (!isMounted) return;

        setError("We couldn't load locations. Please try again in a moment.");
        setLocations([]);
        setFiltered([]);

        gaEvent("locations_load_error", {
          message: err?.message || "unknown",
        });
      }
    };

    fetchLocations();
    return () => {
      isMounted = false;
    };
  }, [userLocation]);

  useEffect(() => {
    const getLocation = async () => {
      if (typeof window === "undefined") return;

      const isNativeCapacitor =
        window.Capacitor &&
        window.Capacitor.getPlatform &&
        window.Capacitor.getPlatform() !== "web";

      try {
        if (isNativeCapacitor) {
          console.log("[HomeScreen] Using Capacitor Geolocation");
          const perm = await Geolocation.requestPermissions();
          console.log("[HomeScreen] Geolocation permissions:", perm);

          const pos = await Geolocation.getCurrentPosition({
            enableHighAccuracy: true,
          });

          const { latitude, longitude } = pos.coords;
          const nextCenter = { lat: latitude, lng: longitude };
          setCenter(nextCenter);
          setUserLocation(nextCenter);
        } else if (navigator.geolocation) {
          console.log("[HomeScreen] Using browser navigator.geolocation");
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              const { latitude, longitude } = pos.coords;
              const nextCenter = { lat: latitude, lng: longitude };
              setCenter(nextCenter);
              setUserLocation(nextCenter);
            },
            (err) => console.warn("[HomeScreen] Browser geo error:", err),
            { enableHighAccuracy: true, timeout: 10000 }
          );
        }
      } catch (err) {
        console.warn("[HomeScreen] Error getting position:", err);
      }
    };

    getLocation();
  }, []);

  const activeLocation = useMemo(
    () => filtered.find((l) => l.id === activeLocationId) || filtered[0] || null,
    [filtered, activeLocationId]
  );

  useEffect(() => {
    setShowDetails(false);
    setDetailsIndex(0);
  }, [activeLocationId]);

  useEffect(() => {
    if (!activeLocationId) return;

    const t = setTimeout(() => {
      const el = rowRefs.current[activeLocationId];
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, 50);

    return () => clearTimeout(t);
  }, [activeLocationId]);

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    const lower = term.toLowerCase();

    const filteredList = locations.filter((loc) => {
      const name = getLocationName(loc).toLowerCase();
      const addr = (loc.address || "").toLowerCase();
      return name.includes(lower) || addr.includes(lower);
    });

    setFiltered(filteredList);

    if (filteredList.length > 0) {
      setActiveLocationId(filteredList[0].id || null);
    } else {
      setActiveLocationId(null);
    }

    if (term.length >= 3) {
      gaEvent("search_change", {
        query_len: term.length,
        results: filteredList.length,
      });
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
    setFiltered(locations);
    if (locations.length > 0) setActiveLocationId(locations[0].id || null);
  };

  const handleSelectLocation = (loc, source = "unknown") => {
    setActiveLocationId(loc.id || null);

    gaEvent("location_select", {
      location_id: loc.id,
      source,
      has_price: getPriceValue(loc) != null,
    });

    if (loc.coordinates && loc.coordinates.lat !== undefined) {
      setCenter({
        lat: toNumber(loc.coordinates.lat) || center.lat,
        lng: toNumber(loc.coordinates.lng) || center.lng,
      });
    }

    expandSheet();
  };

  const redirectToLogin = () => {
    const next = encodeURIComponent(window.location.hash || "#/home");
    window.location.hash = `#/?next=${next}`;
  };

  const handleConfirm = async () => {
    if (!activeLocation) return;

    const token = localStorage.getItem("token");

    if (!token) {
      gaEvent("checkout_redirect_to_login", {
        location_id: activeLocation?.id || "",
      });
      redirectToLogin();
      return;
    }

    const isNativeCapacitor =
      typeof window !== "undefined" &&
      window.Capacitor &&
      window.Capacitor.getPlatform &&
      window.Capacitor.getPlatform() !== "web";

    try {
      setIsConfirming(true);
      setError("");

      const priceValue = getPriceValue(activeLocation);
      if (!priceValue) {
        setError("This location does not have a valid price.");
        return;
      }

      gaEvent("checkout_start", {
        location_id: activeLocation.id,
        price: priceValue,
      });

      const res = await axios.post(
        `${API}/payments/guest/checkout`,
        {
          locationId: activeLocation.id,
          price: priceValue,
          name: getLocationName(activeLocation),
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            ...(isNativeCapacitor ? { "X-Platform": "capacitor" } : {}),
          },
        }
      );

      if (res.data && res.data.url) {
        gaEvent("checkout_url_created", { location_id: activeLocation.id });

        const checkoutUrl = res.data.url;

        try {
          if (isNativeCapacitor) {
            await Browser.open({ url: checkoutUrl, presentationStyle: "fullscreen" });
          } else {
            window.location.href = checkoutUrl;
          }
        } catch (e) {
          console.error("Failed to open Stripe checkout", e);
          window.location.href = checkoutUrl;
        }
      } else {
        setError("We couldn't start your payment. Please try again.");
      }
    } catch (err) {
      console.error("Error starting payment", err);

      gaEvent("checkout_error", {
        location_id: activeLocation?.id || "",
        message: err.response?.data?.error || err?.message || "unknown",
      });

      setError(err.response?.data?.error || "We couldn't start your payment. Please try again.");
    } finally {
      setIsConfirming(false);
    }
  };

  const priceValue = getPriceValue(activeLocation);
  const distanceLabel = getDistanceLabel(activeLocation, userLocation);
  const activeName = getLocationName(activeLocation);

  const activePhotos = getPhotosArray(activeLocation);
  const descriptionText =
    (activeLocation?.description || "").trim() ||
    "No description yet. This location will add details soon.";
  const slidesCount = 1 + activePhotos.length;

  const goPrev = () => setDetailsIndex((i) => Math.max(0, i - 1));
  const goNext = () => setDetailsIndex((i) => Math.min(slidesCount - 1, i + 1));

  return (
    <div className="p2p-airbnb">
      <div className="p2p-topUnderlay" />

      <div className="p2p-mapWrap">
        {!mapsKey ? (
          <div className="p2p-mapFallback">
            Map view is temporarily unavailable. Please try again later.
          </div>
        ) : (
          <GoogleMapReact
            bootstrapURLKeys={{ key: mapsKey }}
            center={center}
            defaultZoom={15}
            yesIWantToUseGoogleMapApiInternals
          >
            {userLocation?.lat != null && userLocation?.lng != null && (
              <UserDot lat={userLocation.lat} lng={userLocation.lng} />
            )}

            {filtered.map((loc) => {
              const c = loc.coordinates;
              const lat = c ? toNumber(c.lat) : null;
              const lng = c ? toNumber(c.lng) : null;
              if (lat == null || lng == null) return null;

              return (
                <MapMarker
                  key={loc.id}
                  lat={lat}
                  lng={lng}
                  selected={loc.id === activeLocationId}
                  onClick={() => handleSelectLocation(loc, "map_pin")}
                />
              );
            })}
          </GoogleMapReact>
        )}

        <div className="p2p-topBar">
          <div className="p2p-search">
            <span className="p2p-search__icon">🔎</span>
            <input
              className="p2p-search__input"
              placeholder="Search by name or address"
              value={searchTerm}
              onChange={handleSearch}
            />
            {searchTerm ? (
              <button type="button" className="p2p-search__clear" onClick={clearSearch}>
                ✕
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div
        ref={sheetRef}
        className="p2p-sheet"
        style={{ transform: `translateY(${sheetTranslate}px)` }}
      >
        <div
          className="p2p-sheet__handleZone"
          onTouchStart={onSheetTouchStart}
          onTouchMove={onSheetTouchMove}
          onTouchEnd={onSheetTouchEnd}
        >
          <div className="p2p-sheet__handle" />
        </div>

        <div className="p2p-sheet__content">
          {error ? <div className="p2p-error">{error}</div> : null}

          {filtered.length === 0 ? (
            <div className="p2p-empty">No locations found.</div>
          ) : (
            <>
              <div className={`p2p-selectedCard ${showDetails ? "p2p-selectedCard--details" : ""}`}>
                {!showDetails ? (
                  <div className="p2p-selectedCard__main">
                    <div className="p2p-selectedCard__title">{activeName}</div>
                    <div className="p2p-selectedCard__sub">{activeLocation?.address || ""}</div>
                    <div className="p2p-selectedCard__meta">
                      {distanceLabel ? <span className="p2p-pill">{distanceLabel}</span> : null}
                      {priceValue != null ? (
                        <span className="p2p-pill">${priceValue.toFixed(2)} / visit</span>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <div className="p2p-selectedCard__main">
                    <div className="p2p-detailsSlider">
                      {detailsIndex === 0 ? (
                        <div
                          className="p2p-detailsSlide p2p-detailsSlide--text"
                          style={{
                            padding: 14,
                            background: "rgba(0,0,0,0.35)",
                            color: "white",
                            minHeight: 160,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-start",
                          }}
                        >
                          <div style={{ fontWeight: 800, fontSize: 14, opacity: 0.95 }}>
                            About this bathroom
                          </div>
                          <div style={{ marginTop: 8, fontSize: 13, lineHeight: 1.35 }}>
                            {descriptionText}
                          </div>
                        </div>
                      ) : (
                        <img
                          className="p2p-detailsPhoto"
                          src={activePhotos[detailsIndex - 1]}
                          alt={`Photo ${detailsIndex}`}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      )}

                      <div
                        className="p2p-detailsOverlay"
                        style={{
                          position: "absolute",
                          left: 10,
                          right: 10,
                          bottom: 10,
                          padding: "10px 10px",
                          borderRadius: 12,
                          background: "rgba(0,0,0,0.45)",
                          color: "#fff",
                          backdropFilter: "blur(6px)",
                        }}
                      >
                        <div style={{ fontWeight: 900, fontSize: 14 }}>{activeName}</div>
                        <div style={{ fontSize: 12, opacity: 0.9 }}>
                          {activeLocation?.address || ""}
                        </div>
                        <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap" }}>
                          {distanceLabel ? <span className="p2p-pill">{distanceLabel}</span> : null}
                          {priceValue != null ? (
                            <span className="p2p-pill">${priceValue.toFixed(2)} / visit</span>
                          ) : null}
                        </div>
                      </div>

                      {slidesCount > 1 ? (
                        <>
                          <button
                            type="button"
                            onClick={goPrev}
                            disabled={detailsIndex === 0}
                            className="p2p-detailsArrow p2p-detailsArrow--left"
                            style={{
                              position: "absolute",
                              top: "50%",
                              left: 8,
                              transform: "translateY(-50%)",
                              width: 34,
                              height: 34,
                              borderRadius: 999,
                              border: "none",
                              background: "rgba(0,0,0,0.45)",
                              color: "white",
                              cursor: "pointer",
                            }}
                            aria-label="Previous"
                            title="Previous"
                          >
                            ‹
                          </button>

                          <button
                            type="button"
                            onClick={goNext}
                            disabled={detailsIndex === slidesCount - 1}
                            style={{
                              position: "absolute",
                              top: "50%",
                              right: 8,
                              transform: "translateY(-50%)",
                              width: 34,
                              height: 34,
                              borderRadius: 999,
                              border: "none",
                              background: "rgba(0,0,0,0.45)",
                              color: "white",
                              cursor: "pointer",
                            }}
                            aria-label="Next"
                            title="Next"
                          >
                            ›
                          </button>

                          <div className="p2p-detailsDots">
                            {Array.from({ length: slidesCount }).map((_, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() => setDetailsIndex(i)}
                                className={
                                  i === detailsIndex
                                    ? "p2p-detailsDot p2p-detailsDot--active"
                                    : "p2p-detailsDot"
                                }
                                aria-label={`Slide ${i + 1}`}
                              />
                            ))}
                          </div>
                        </>
                      ) : null}
                    </div>
                  </div>
                )}

                <div className="p2p-selectedCard__actions">
                  <button
                    type="button"
                    className="p2p-miniBtn"
                    onClick={() => {
                      setShowDetails(false);
                      setDetailsIndex(0);
                      collapseSheet();
                    }}
                  >
                    List
                  </button>

                  <button
                    type="button"
                    className="p2p-miniBtn"
                    onClick={() => {
                      setShowDetails((v) => !v);
                      setDetailsIndex(0);
                      expandSheet();
                    }}
                  >
                    {showDetails ? "Hide" : "Details"}
                  </button>
                </div>
              </div>

              <div className="p2p-list" ref={listRef}>
                {filtered.map((loc) => {
                  const name = getLocationName(loc);
                  const price = getPriceValue(loc);
                  const dist = getDistanceLabel(loc, userLocation);
                  const isActive = loc.id === activeLocationId;

                  return (
                    <button
                      key={loc.id}
                      type="button"
                      ref={(el) => {
                        if (el) rowRefs.current[loc.id] = el;
                      }}
                      className={isActive ? "p2p-row p2p-row--active" : "p2p-row"}
                      onClick={() => handleSelectLocation(loc, "list_row")}
                    >
                      <div className="p2p-row__left">
                        <div className="p2p-row__name">{name}</div>
                        <div className="p2p-row__addr">{loc.address || ""}</div>
                        <div className="p2p-row__meta">{dist ? <span>{dist}</span> : null}</div>
                      </div>
                      <div className="p2p-row__right">
                        {price != null ? (
                          <div className="p2p-row__price">${price.toFixed(2)}</div>
                        ) : null}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="p2p-ctaBar">
                <button
                  type="button"
                  className="p2p-ctaBtn"
                  onClick={handleConfirm}
                  disabled={isConfirming || !activeLocation}
                >
                  {isConfirming
                    ? "Starting your payment..."
                    : "Confirm & Continue"}
                </button>
                <div className="p2p-ctaHint">
                  You won&apos;t be charged until payment is confirmed on the next screen.
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}